// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import Select from 'presentations/views/components/atoms/Select';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import Switch from 'presentations/views/components/atoms/Switch';
import Tooltip from 'presentations/views/components/atoms/Tooltip';
import Popover from 'presentations/views/components/atoms/Popover';
import styled from '@emotion/styled';
import { isMediaMax, mediaMax, spacing } from 'presentations/views/styles';
import { black, gray01, white } from 'presentations/views/styles/color';
import { SyntheticEvent, useState } from 'react';
import HeaderNotificationBar from 'presentations/views/components/molecules/HeaderNotificationBar';

export enum PreviewMode {
  Host = 0,
  Guest = 1,
}

export interface Props {
  previewMode: PreviewMode;
  onChangePreviewMode: (e: SyntheticEvent<HTMLSelectElement>) => void;
  isInitLoaded: boolean;
  url: string;
  isHostDiagnosticMode: boolean;
  isDebuggable: boolean;
  onClickHostDianosticMode: () => void;
}
export default function Component(props: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isMobile = isMediaMax('tablet');

  const t = translateInComponent(translations);
  const onClose = () => {
    setAnchor(null);
  };
  const onClickMenu = (e: SyntheticEvent<HTMLButtonElement>) => {
    setAnchor(e.currentTarget);
  };
  const renderContent = () => {
    return (
      <Content>
        <NotificationSection>
          <StyledTooltip title={t('Preview mode description')}>
            <StyledIcon name="monitor" />
            <StyledSelect
              value={props.previewMode}
              onChange={props.onChangePreviewMode}
              size="small"
              variant="standard"
              options={[
                { text: t('Host'), value: 0 },
                { text: t('Guest'), value: 1 },
              ]}
            />
          </StyledTooltip>
        </NotificationSection>
        <NotificationSection>
          <StyledLink href={props.url}>
            <StyledTooltip title={t('Edit description')}>
              <StyledIcon name="edit" />
              <NotificationSectionTitle>{t('Edit')}</NotificationSectionTitle>
            </StyledTooltip>
          </StyledLink>
        </NotificationSection>
        {props.isInitLoaded && props.isDebuggable && (
          <NotificationSection>
            <StyledTooltip title={t('Debug description')}>
              <StyledIcon name="code-tags" />
              <NotificationSectionTitle>{t('Debug')}</NotificationSectionTitle>
              <StyledSwitch
                isChecked={props.isHostDiagnosticMode}
                onChange={props.onClickHostDianosticMode}
              />
            </StyledTooltip>
          </NotificationSection>
        )}
      </Content>
    );
  };

  return (
    <HeaderNotificationBar
      notificationType={'info'}
      title={t('Preview')}
      onClickMenu={isMobile ? onClickMenu : undefined}
      content={
        isMobile ? (
          <StyledPopover
            onClose={onClose}
            anchor={anchor}
            maxHeight={isMobile ? 400 : undefined}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            isKeepMounted={false}
          >
            {renderContent()}
          </StyledPopover>
        ) : (
          renderContent()
        )
      }
    />
  );
}

const Content = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    padding: ${spacing(16)} ${spacing(16)};
    flex-direction: column;
    align-items: flex-start;
  }
`;
const NotificationSection = styled.div`
  margin-right: ${spacing(20)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-right: 0;
    margin-bottom: ${spacing(16)};
    :last-of-type {
      margin-bottom: 0;
    }
  }
`;
const NotificationSectionTitle = styled.div`
  margin-right: ${spacing(8)};
  color: ${white};
`;
const StyledSelect = styled(Select)`
  height: 28px;
  background-color: ${black};
  display: flex;
  align-items: center;
  height: 100%;
  .MuiInputBase-root {
    color: ${white};
    svg {
      color: ${white} !important;
    }
    select {
      padding: 0;
    }
  }
  .MuiInput-underline:before {
    display: none;
  }
  ${mediaMax.tablet} {
    background-color: ${gray01};
  }
`;
const StyledLink = styled(Link)`
  color: ${white};
`;
const StyledIcon = styled(Icon)`
  margin-right: ${spacing(8)};
  width: 18px !important;
  height: 18px !important;
  color: ${white};
`;
const StyledTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
`;
const StyledSwitch = styled(Switch)`
  display: flex;
  align-items: center;
`;

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    background-color: ${gray01};
  }
`;
